// The event create/edit form: name, start and end in a time zone,
// description, and a meeting point picked on the map.
import { lazy, Suspense, useMemo, useState, type FormEvent } from "react";
import type { Schemas } from "@10twenty/api";
import { DESCRIPTION_MAX, draftProblem, LABEL_MAX, timeZoneChoices, type EventDraft } from "../events/events";

type CircleEvent = Schemas["CircleEvent"];
import { useNow } from "../useNow";

const PointMap = lazy(() => import("../map/PointMap"));

/** Circle names, as in the contract. */
const NAME_MAX = 80;
const GEOLOCATION_TIMEOUT_MS = 30_000;
/** Rechecks "must end in the future" this often. */
const NOW_TICK_MS = 30_000;

export function EventForm(props: {
  initial: EventDraft;
  /** An ended event: its start and end can no longer move. */
  timesFixed?: boolean;
  /** The event being edited: its untouched times are checked as they are. */
  original?: CircleEvent;
  submitLabel: string;
  busy: boolean;
  error: string | null;
  onSubmit: (draft: EventDraft) => void;
  onCancel?: () => void;
}) {
  const [draft, setDraft] = useState(props.initial);
  const [shown, setShown] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState<string | null>(null);
  const zones = useMemo(() => timeZoneChoices(draft.timeZone), [draft.timeZone]);
  const nowMs = useNow(NOW_TICK_MS);
  const problem = draftProblem(draft, nowMs, props.timesFixed, props.original);
  const set = (patch: Partial<EventDraft>) => setDraft((prev) => ({ ...prev, ...patch }));

  function submit(event: FormEvent) {
    event.preventDefault();
    setShown(true);
    if (!draftProblem(draft, Date.now(), props.timesFixed, props.original)) props.onSubmit(draft);
  }

  function locateMe() {
    if (!("geolocation" in navigator)) {
      setLocateError("This browser can't share its location. Click the map instead.");
      return;
    }
    setLocating(true);
    setLocateError(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocating(false);
        set({ meetingPoint: { lat: position.coords.latitude, lon: position.coords.longitude } });
      },
      () => {
        setLocating(false);
        setLocateError("Couldn't get your location. Click the map instead.");
      },
      { enableHighAccuracy: true, timeout: GEOLOCATION_TIMEOUT_MS, maximumAge: 60_000 },
    );
  }

  return (
    <form onSubmit={submit} className="stack event-form" noValidate>
      {(props.error ?? (shown && problem)) && (
        <p className="error" role="alert">
          {props.error ?? problem}
        </p>
      )}
      <label>
        Name
        <input
          required
          maxLength={NAME_MAX}
          placeholder="Sam's birthday picnic"
          value={draft.name}
          onChange={(event) => set({ name: event.target.value })}
        />
      </label>
      <div className="event-form-times">
        <label>
          Starts
          <input
            type="datetime-local"
            required
            disabled={props.timesFixed}
            value={draft.start}
            onChange={(event) => set({ start: event.target.value })}
          />
        </label>
        <label>
          Ends
          <input
            type="datetime-local"
            required
            disabled={props.timesFixed}
            value={draft.end}
            onChange={(event) => set({ end: event.target.value })}
          />
        </label>
        <label>
          Time zone
          <select value={draft.timeZone} onChange={(event) => set({ timeZone: event.target.value })}>
            {zones.map((zone) => (
              <option key={zone} value={zone}>
                {zone.replaceAll("_", " ")}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="hint">
        {props.timesFixed
          ? "The event has ended, so its times can no longer change."
          : "Up to 14 days. Members see each other's locations only between the start and the end."}
      </p>
      <label>
        Description (optional)
        <textarea
          rows={3}
          value={draft.description}
          onChange={(event) => set({ description: event.target.value })}
          aria-describedby="event-description-count"
        />
      </label>
      <span id="event-description-count" className="hint">
        {[...draft.description.trim()].length} / {DESCRIPTION_MAX}
      </span>
      <fieldset className="stack event-meeting">
        <legend>Meeting point (optional)</legend>
        <p className="hint">Click the map to place it{draft.meetingPoint ? ", or click again to move it" : ""}.</p>
        <Suspense fallback={<div className="point-map" />}>
          <PointMap
            point={draft.meetingPoint}
            onPick={(lat, lon) => set({ meetingPoint: { lat, lon } })}
            label="Map for choosing the meeting point"
          />
        </Suspense>
        {locateError && <p className="error">{locateError}</p>}
        <div className="button-row">
          <button type="button" className="secondary" disabled={locating} onClick={locateMe}>
            {locating ? "Finding you…" : "Use my location"}
          </button>
          {draft.meetingPoint && (
            <button type="button" className="secondary" onClick={() => set({ meetingPoint: null, meetingLabel: "" })}>
              Remove meeting point
            </button>
          )}
        </div>
        {draft.meetingPoint && (
          <label>
            Meeting point name (optional)
            <input
              maxLength={LABEL_MAX}
              placeholder="Main gate"
              value={draft.meetingLabel}
              onChange={(event) => set({ meetingLabel: event.target.value })}
            />
          </label>
        )}
      </fieldset>
      <div className="button-row">
        <button type="submit" disabled={props.busy}>
          {props.submitLabel}
        </button>
        {props.onCancel && (
          <button type="button" className="secondary" onClick={props.onCancel}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
